import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import {
  ContentSearchAutocompleteHeaders,
  ContentSearchAutocompleteParameters,
} from '@/integrations/content-search/content-search-autocomplete/content-search-autocomplete.fetch';
import getCulture from '@/libraries/getters/get-culture';
import getDomain from '@/libraries/getters/get-domain';
import getLocale from '@/libraries/getters/get-locale';

export type UseQueryContentSearchAutocompleteData = string[];

interface UseQueryContentSearchAutocompleteParameters
  extends ContentSearchAutocompleteParameters {}

interface UseQueryContentSearchAutocompleteHeaders
  extends ContentSearchAutocompleteHeaders {}

interface UseQueryContentSearchAutocompleteOptions {
  enabled?: boolean;
  initialData?: UseQueryContentSearchAutocompleteData;
}

export const useQueryContentSearchAutocomplete = (
  parameters: UseQueryContentSearchAutocompleteParameters,
  customHeaders: UseQueryContentSearchAutocompleteHeaders,
  options?: UseQueryContentSearchAutocompleteOptions,
) => {
  const locale = getLocale();

  const query = useQuery<UseQueryContentSearchAutocompleteData>(
    [QueryId.CONTENT_SEARCH, parameters, customHeaders, locale],
    async () => {
      const fetchResponse = await fetch('/api/content-search/autocomplete', {
        body: JSON.stringify({
          headers: customHeaders,
          parameters,
        }),
        headers: {
          ['Accept-Language']: getCulture(locale),
          ['Content-Type']: 'application/json',
          ['Ocp-Apim-Subscription-Key']: `${process.env.APIM_SUBSCRIPTION_KEY}`,
          ['X-Domain-Host']: getDomain(),
        },
        method: 'POST',
      })
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryContentSearchAutocomplete', error);
          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: options?.enabled,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};
